// 레이드 HUD(RAID-PRD 9절): 왼쪽 목표 패널 아래 파티 창(용병 3명 초상·이름·HP·상태, 비룡의 표적 표시)과
// 머리 줄(페이즈·광폭 시계·보상/연습). 비룡이 날면 머리 줄이 붉은 경고로 바뀐다(깜빡임 없음). 입력은 받지 않는다(pointer-events 없음).
// 값이 바뀔 때만 DOM을 고친다(프레임마다 문자열을 만들지 않게 이전 값과 비교).
import type { GameCommand, GameView } from '../shared/types';
import { Scene } from '../shared/types';
import { MERCS } from '../shared/content/raid';
import { assetUrl } from '../pwa/assetMap';
import { FONT, FONT_NUM, TEXT_OUTLINE, el } from './dom';
import { viewH, viewRect, viewW, type ViewRect } from '../shared/viewport';
import { RAID_BRAND_H, RAID_LEAVE_MIN_H, RAID_LEAVE_W, raidBrandBandRect, raidBrandText, raidLayoutRev, raidLeaveRect, raidMiniTop, raidPartyLayout, raidRowKey, type RaidPartyMode } from './raidHudLayout';
import { RAID_LEAVE_ARM_MS, enrageText, phaseText, raidLeaveStep } from './raidModel';

const ROLE_COLOR: Record<string, string> = { tank: '#6aa8ff', archer: '#7fe070', priest: '#ffe08a' };
const W = 236;
/** 왼쪽 띠(목표 카드·자동 버튼·자동 이동 알약·방 요령): 파티 창은 이들의 맨 아래에 붙는다 */
const LEFT_IDS = ['hud-objective', 'hud-auto', 'auto-status', 'room-tip'];

interface Row { nl: HTMLDivElement; bar: HTMLDivElement; pw: HTMLDivElement; box: HTMLDivElement; fill: HTMLDivElement; hpText: HTMLSpanElement; state: HTMLSpanElement; mark: HTMLSpanElement; img: HTMLImageElement; key: string; shown: boolean }

export class RaidHud {
  private readonly box: HTMLDivElement;
  private readonly head: HTMLDivElement;
  private readonly phase: HTMLSpanElement;
  private readonly clock: HTMLSpanElement;
  private readonly mode: HTMLSpanElement;
  private readonly heroMark: HTMLDivElement;
  private readonly rows: Row[] = [];
  private shown = false;
  private headKey = '';

  private readonly leaveBtn: HTMLButtonElement;
  private leaveArmedAt = 0;
  private leaveShown = false;
  private readonly waitBand: HTMLDivElement;
  private waitShown = false;
  /** 하드 잿불 낙인 경고 띠(영웅에게 붙었을 때만, 위쪽 가장자리 가운데)와 남은 시간 글자 */
  private readonly brandBand: HTMLDivElement;
  private readonly brandTime: HTMLSpanElement;
  private brandShown = false;
  private brandKey = '';
  private brandFrame = 0;
  /** 보스 이름 옆 「하드」 배지(hud.ts 보스 체력 줄의 이름 줄에 붙인다) */
  private readonly hardBadge: HTMLSpanElement;
  private hardShown = false;
  private pmode: RaidPartyMode | '' = '';
  private frame = 0;
  private lastW = 0;
  private lastH = 0;
  private placeKey = '';
  private fullH = 0;
  private compactH = 0;
  private miniH = 0;
  private miniW = 0;

  constructor(private readonly parent: HTMLElement, send?: (c: GameCommand) => void, private readonly touch = false) {
    // 4인 판 「나가기」: 오른쪽 위 설정(톱니) 바로 왼쪽(raidLeaveRect). 왼쪽 띠의 자동 버튼·목표·플레이 영역·조이스틱을 가리지 않는다. 두 번 눌러야 나간다
    this.leaveBtn = el('button', 'position:absolute;left:0;top:0;box-sizing:border-box;width:' + RAID_LEAVE_W + 'px;height:' + RAID_LEAVE_MIN_H + 'px;display:none;padding:0 6px;border-radius:10px;border:1px solid rgba(255,255,255,.3);background:rgba(20,10,14,.78);color:#ffd2c4;font:800 12px ' + FONT + ';white-space:nowrap;pointer-events:auto;z-index:6;', parent, '나가기');
    this.waitBand = el('div', 'position:absolute;left:50%;top:4px;transform:translateX(-50%);display:none;padding:3px 12px;border-radius:0 0 10px 10px;background:rgba(20,14,30,.82);border:1px solid rgba(255,215,106,.6);border-top:none;font:800 12px ' + FONT + ';color:#ffe9a8;pointer-events:none;z-index:6;', parent, '동료를 기다리는 중…');
    this.waitBand.dataset.testid = 'raid-net-wait';
    // 잿불 낙인 띠: 켜고 끌 뿐 깜빡이지 않는다(섬광 규칙). 마지막 1.5초는 테두리·글자색만 더 붉게 바뀐다
    this.brandBand = el('div', 'position:absolute;left:0;top:0;display:none;align-items:center;gap:8px;box-sizing:border-box;height:' + RAID_BRAND_H + 'px;padding:0 12px;border-radius:7px;white-space:nowrap;transform-origin:0 0;' +
      'background:linear-gradient(180deg,rgba(60,10,6,.93),rgba(24,6,8,.93));border:2px solid #ff6a1c;box-shadow:0 3px 10px rgba(0,0,0,.55);pointer-events:none;z-index:7;', parent);
    this.brandBand.dataset.testid = 'raid-brand-band';
    this.brandBand.setAttribute('role', 'alert');
    el('span', 'font:900 14px ' + FONT + ';letter-spacing:1px;color:#ffb040;' + TEXT_OUTLINE, this.brandBand, '잿불 낙인');
    el('span', 'font:800 13px ' + FONT + ';color:#f4ecd8;' + TEXT_OUTLINE, this.brandBand, '동료에게서 떨어지세요');
    this.brandTime = el('span', 'display:inline-block;min-width:3.4em;text-align:right;font:900 14px ' + FONT_NUM + ';color:#fff;' + TEXT_OUTLINE, this.brandBand, '');
    this.brandTime.dataset.testid = 'raid-brand-time';
    this.hardBadge = el('span', 'display:none;font:900 12px ' + FONT + ';letter-spacing:2px;color:#fff;border-radius:6px;padding:1px 8px;background:linear-gradient(#ff7a2a,#b01a10);border:1px solid #ffc080;text-shadow:0 1px 2px #000;');
    this.hardBadge.textContent = '하드';
    this.hardBadge.dataset.testid = 'raid-hard-badge';
    this.leaveBtn.type = 'button'; this.leaveBtn.dataset.testid = 'raid-net-leave-band';
    this.leaveBtn.addEventListener('click', () => {
      const now = Date.now(), r = raidLeaveStep(now, this.leaveArmedAt);
      if (r.leave) { this.leaveArmedAt = 0; this.leaveBtn.textContent = '나가기'; send?.({ type: 'raidNetLeave' }); return; }
      this.leaveArmedAt = now; this.leaveBtn.textContent = '정말 나가기?';
      window.setTimeout(() => { if (this.leaveArmedAt === now) { this.leaveArmedAt = 0; this.leaveBtn.textContent = '나가기'; } }, RAID_LEAVE_ARM_MS);
    });
    this.box = el('div', 'position:absolute;left:8px;top:240px;width:' + W + 'px;display:none;flex-direction:column;gap:4px;pointer-events:none;transform-origin:0 0;z-index:6;', parent);
    this.box.dataset.testid = 'raid-hud';
    this.head = el('div', 'display:flex;flex-direction:column;gap:1px;padding:5px 9px;border-radius:8px;border:1.5px solid rgba(255,140,80,.7);background:linear-gradient(180deg,rgba(40,12,10,.88),rgba(16,8,14,.88));', this.box);
    const l1 = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:6px;', this.head);
    this.phase = el('span', 'font:900 12.5px ' + FONT + ';color:#ffd2b0;' + TEXT_OUTLINE, l1, '');
    this.phase.dataset.testid = 'raid-phase';
    this.clock = el('span', 'font:900 13px ' + FONT_NUM + ';color:#fff;letter-spacing:.5px;white-space:nowrap;' + TEXT_OUTLINE, l1, '');
    this.clock.dataset.testid = 'raid-enrage';
    this.mode = el('span', 'font:800 11px ' + FONT + ';color:#aeb9da;', this.head, '');
    this.mode.dataset.testid = 'raid-mode';
    // 영웅이 표적일 때 머리 줄 아래 작은 경고
    this.heroMark = el('div', 'display:none;padding:2px 9px;border-radius:6px;background:rgba(120,16,10,.85);border:1px solid #ff4a3a;font:900 12px ' + FONT + ';color:#fff;' + TEXT_OUTLINE, this.box, '비룡이 당신을 노린다!');
    this.heroMark.dataset.testid = 'raid-target-hero';
    for (let i = 0; i < MERCS.length; i++) {
      const d = MERCS[i], col = ROLE_COLOR[d.role];
      const box = el('div', 'position:relative;display:flex;align-items:center;gap:7px;padding:4px 8px 4px 4px;border-radius:8px;background:rgba(10,14,30,.82);border:1px solid ' + col + '77;', this.box);
      box.dataset.testid = 'raid-merc-' + d.role;
      const pw = el('div', 'flex:0 0 auto;width:32px;height:32px;box-sizing:content-box;border-radius:50%;overflow:hidden;border:2px solid ' + col + ';background:#0b1020;', box);
      const img = el('img', 'width:100%;height:100%;object-fit:cover;display:block;', pw);
      img.alt = ''; img.draggable = false; img.src = assetUrl('ui/raid/merc_' + d.role + '.webp');
      img.onerror = () => { img.style.visibility = 'hidden'; };
      const t = el('div', 'flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;', box);
      const nl = el('div', 'display:flex;align-items:center;gap:5px;', t);
      el('span', 'font:900 12px ' + FONT + ';color:' + col + ';' + TEXT_OUTLINE, nl, d.name);
      const state = el('span', 'font:800 10.5px ' + FONT + ';color:#cfd8f2;white-space:nowrap;', nl, '');
      const mark = el('span', 'display:none;margin-left:auto;padding:0 6px;border-radius:5px;background:#c0202c;font:900 10px ' + FONT + ';color:#fff;', nl, '표적');
      const bar = el('div', 'position:relative;height:9px;border-radius:3px;background:#0b1020;border:1px solid rgba(255,255,255,.25);overflow:hidden;', t);
      const fill = el('div', 'height:100%;width:100%;background:linear-gradient(90deg,#3fae55,#7fe070);', bar);
      const hpText = el('span', 'position:absolute;right:3px;top:-3px;font:900 9.5px ' + FONT_NUM + ';color:#fff;' + TEXT_OUTLINE, bar, '');
      this.rows.push({ nl, bar, pw, box, fill, hpText, state, mark, img, key: '', shown: true });
    }
  }

  /** u = HUD 배율(디자인 1280×720 기준) */
  update(v: GameView, u: number): void {
    const r = v.raid;
    const on = !!r && r.active && v.flow.scene === Scene.Dungeon;
    const net = on && !!v.raidNet && v.raidNet.active;
    const wait = net && !!v.raidNet && v.raidNet.barrier;
    if (wait !== this.waitShown) { this.waitShown = wait; this.waitBand.style.display = wait ? 'block' : 'none'; }
    if (net !== this.leaveShown) { this.leaveShown = net; this.leaveBtn.style.display = net ? 'block' : 'none'; if (!net) { this.leaveArmedAt = 0; this.leaveBtn.textContent = '나가기'; } }
    if (on !== this.shown) { this.shown = on; this.box.style.display = on ? 'flex' : 'none'; }
    this.hard(on && !!r && r.diff === 'hard');
    this.brand(on && r ? r : null, u);
    if (!on || !r) return;
    this.place(u);
    const b = v.boss, fighting = b.state !== 0 && b.state !== 1 && b.hp > 0;
    const air = r.air > 0;
    const hk = r.phase + ':' + (air ? 1 : 0) + ':' + r.enraged + ':' + Math.ceil(r.enrageIn / 60) + ':' + r.practice + ':' + r.weekClears;
    if (hk !== this.headKey) {
      this.headKey = hk;
      this.phase.textContent = phaseText(r.phase, r.air);
      this.phase.style.color = air ? '#ff8a6a' : '#ffd2b0';
      this.head.style.borderColor = air || r.enraged ? '#ff4a1c' : 'rgba(255,140,80,.7)';
      // 등장 전·등장 중에는 시계가 아직 돌지 않는다: 판의 처음 값(노말 6:00·하드 7:00 — prepare가 난이도로 정한 enrageIn)을 그대로 보여 준다(#336 리뷰 P2-4)
      this.clock.textContent = enrageText(r.enraged, r.enrageIn);
      this.clock.style.color = r.enraged ? '#ff6a4a' : r.enrageIn < 3600 ? '#ffd76a' : '#fff';
      this.mode.textContent = r.practice ? '연습 · 이번 주 보상을 모두 받았어요' : '보상 판 ' + (r.weekClears + 1) + '/' + r.weekLimit;
    }
    const heroTarget = fighting && r.target === 0;
    // 한 줄 모양에는 붙일 자리가 없어 머리 줄 테두리를 붉게 바꿔 알린다
    this.heroMark.style.display = heroTarget && this.pmode !== 'mini' ? 'block' : 'none';
    if (this.pmode === 'mini') this.head.style.borderColor = heroTarget ? '#ff2a2a' : air || r.enraged ? '#ff4a1c' : 'rgba(255,140,80,.7)';
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i], m = r.mercs[i];
      if (!m) continue;
      // 넷 판: 사람이 차지한 용병 칸은 숨긴다
      const vis = m.visible !== false;
      if (vis !== row.shown) { row.shown = vis; row.box.style.display = vis ? 'flex' : 'none'; }
      if (!vis) continue;
      const ratio = m.maxHp > 0 ? Math.max(0, Math.min(1, m.hp / m.maxHp)) : 0;
      const down = m.down > 0;
      const taunting = i === 0 && r.tauntTicks > 0;
      const targeted = fighting && r.target === i + 1 && !down;
      const st = down ? '쓰러짐 ' + Math.ceil(m.down / 60) + '초' : taunting ? '도발 중' : m.evading ? '회피' : '';
      const key = raidRowKey(ratio, st, targeted, this.pmode);
      if (key === row.key) continue;
      row.key = key;
      row.fill.style.width = (ratio * 100).toFixed(1) + '%';
      row.fill.style.background = ratio > 0.5 ? 'linear-gradient(90deg,#3fae55,#7fe070)' : ratio > 0.25 ? 'linear-gradient(90deg,#c88a1a,#ffd76a)' : 'linear-gradient(90deg,#a01a1a,#ff5a3a)';
      row.hpText.textContent = Math.round(ratio * 100) + '%';
      row.state.textContent = st;
      row.state.style.color = down ? '#ff8a7a' : taunting ? '#9cc4ff' : '#cfd8f2';
      row.mark.style.display = targeted ? 'inline-block' : 'none';
      row.box.style.outline = targeted && this.pmode === 'mini' ? '2px solid #ff4a3a' : '';
      row.img.style.filter = down ? 'grayscale(1) brightness(.6)' : '';
      row.box.style.opacity = down ? '.7' : '1';
    }
  }
  /** 「하드」 배지: 보스 체력 줄 이름 옆(hud.ts가 만든 이름 줄의 두 번째 자리). 이름 줄이 없으면 붙을 때까지 기다린다 */
  private hard(on: boolean): void {
    if (on && !this.hardBadge.isConnected) {
      const row = this.parent.querySelector<HTMLElement>('[data-testid="boss-bar"]')?.firstElementChild;
      if (row) row.insertBefore(this.hardBadge, row.children[1] ?? null);
    }
    if (on !== this.hardShown) { this.hardShown = on; this.hardBadge.style.display = on ? 'inline-block' : 'none'; this.placeKey = ''; }
  }

  /**
   * 잿불 낙인 경고 띠: 영웅 칸(비트 1)에 낙인이 붙어 있는 동안 위쪽 가장자리 가운데에 「잿불 낙인 · 동료에게서 떨어지세요 n.n초」.
   * 글자는 0.1초가 바뀔 때만, 자리는 켜질 때와 몇 프레임마다만 고친다(raidBrandBandRect — 플레이 영역에 내려오지 않는다)
   */
  private readonly brandRect: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly brandOut = { x: 0, y: 0, w: 0, h: 0 };
  private readonly brandObs: { x: number; y: number; w: number; h: number }[] = [{ x: 0, y: 0, w: 0, h: 0 }, { x: 0, y: 0, w: 0, h: 0 }];
  private brand(r: NonNullable<GameView['raid']> | null, u: number): void {
    const left = r ? r.brandLeft ?? 0 : 0;
    const show = !!r && ((r.brandSlots ?? 0) & 1) !== 0 && left > 0;
    if (show !== this.brandShown) { this.brandShown = show; this.brandBand.style.display = show ? 'flex' : 'none'; this.brandPk[0] = -1; this.brandFrame = 0; }
    if (!show) return;
    const txt = raidBrandText(left), late = left <= 90;
    const key = txt + (late ? '!' : '');
    if (key !== this.brandKey) {
      this.brandKey = key;
      this.brandTime.textContent = txt;
      this.brandTime.style.color = late ? '#ff8a6a' : '#fff';
      this.brandBand.style.borderColor = late ? '#ff2a10' : '#ff6a1c';
    }
    if (this.brandFrame++ % 8 !== 0) return;
    const W = viewW(), sc = Math.max(0.75, Math.min(1.25, u));
    let boss: { x: number; y: number; w: number; h: number } | null = null, nameH = 26 * u;
    // 요소는 찾은 뒤 캐시한다(문서에서 빠졌을 때만 다시 찾는다 — #336 리뷰 P2-4)
    if (!this.bossBarEl || !this.bossBarEl.isConnected) this.bossBarEl = this.parent.querySelector<HTMLElement>('[data-testid="boss-bar"]');
    if (!this.topLeftEl || !this.topLeftEl.isConnected) this.topLeftEl = this.parent.querySelector<HTMLElement>('[data-testid="hud-top-left"]');
    const be = this.bossBarEl;
    if (be && be.offsetWidth) {
      const b = viewRect(be, this.brandRect); boss = this.bossBox; boss.x = b.left; boss.y = b.top; boss.w = b.width; boss.h = b.height;
      const row = be.firstElementChild as HTMLElement | null;
      if (row) nameH = viewRect(row, this.brandRect).height;
    }
    // 비켜야 할 것: 나가기 단추(넷 판)·왼쪽 위 영웅 패널
    const o0 = this.brandObs[0], o1 = this.brandObs[1];
    if (this.leaveShown) { const lr = viewRect(this.leaveBtn, this.brandRect); o0.x = lr.left; o0.y = lr.top; o0.w = lr.width; o0.h = lr.height; } else o0.w = 0;
    const tl = this.topLeftEl;
    if (tl) { const t = viewRect(tl, this.brandRect); o1.x = t.left; o1.y = t.top; o1.w = t.width; o1.h = t.height; } else o1.w = 0;
    const bw = this.brandBand.offsetWidth * sc, bh = RAID_BRAND_H * sc;
    const where = raidBrandBandRect(W, bw, bh, boss, nameH, this.brandObs, this.brandOut);
    // 자리 열쇠는 숫자로만 비교한다(문자열을 만들지 않는다)
    const ks = Math.round(sc * 1000), kx = Math.round(this.brandOut.x), ky = Math.round(this.brandOut.y);
    if (W === this.brandPk[0] && ks === this.brandPk[1] && kx === this.brandPk[2] && ky === this.brandPk[3]) return;
    this.brandPk[0] = W; this.brandPk[1] = ks; this.brandPk[2] = kx; this.brandPk[3] = ky;
    this.brandBand.style.transform = 'scale(' + sc.toFixed(3) + ')';
    this.brandBand.style.left = Math.round(this.brandOut.x) + 'px';
    this.brandBand.style.top = Math.round(this.brandOut.y) + 'px';
    this.brandBand.dataset.place = where;
  }

  /** 왼쪽 띠에 이미 놓인 것(목표·자동 버튼·자동 이동 알약·방 요령)의 가장 아래 끝(view 좌표, 없으면 0). 가운데로 올라간 알약은 왼쪽 띠가 아니라서 뺀다 */
  /** 낙인 띠 자리 계산에 쓰는 요소 캐시·마지막 자리(W·배율×1000·x·y) */
  private bossBarEl: HTMLElement | null = null;
  private topLeftEl: HTMLElement | null = null;
  private readonly brandPk = new Float64Array([-1, -1, -1, -1]);
  /** 측정 재사용 저장소(프레임 루프 할당 없음, AGENTS.md) */
  private readonly mr: ViewRect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private readonly bossBox = { x: 0, y: 0, w: 0, h: 0 };
  private readonly topLeftBox = { x: 6, y: 4, w: 0, h: 66 };
  private leftBottom(W: number): number {
    let b = 0;
    for (const id of LEFT_IDS) {
      const e = this.parent.querySelector<HTMLElement>('[data-testid="' + id + '"]');
      if (!e || !e.offsetWidth || !e.offsetHeight || e.style.display === 'none' || e.style.visibility === 'hidden') continue;
      const cs = getComputedStyle(e);
      if (cs.opacity === '0' || cs.visibility === 'hidden') continue;
      const r = viewRect(e, this.mr);
      if (r.left < W / 3 && r.bottom > b) b = r.bottom;
    }
    return b;
  }

  /** 나가기 단추·파티 창 자리. 화면 크기·배율·왼쪽 띠 아래 끝·보이는 줄 수가 바뀔 때만 다시 잡는다(몇 프레임마다 읽기) */
  private place(u: number): void {
    this.frame++;
    const W = viewW(), H = viewH();
    // 화면 크기가 바뀌면 바로, 아니면 몇 프레임마다만 읽는다(느린 기기에서 창 크기 변경이 늦게 반영되지 않게)
    if (W !== this.lastW || H !== this.lastH) { this.lastW = W; this.lastH = H; this.frame = 1; }
    if (this.frame % 8 !== 1 && this.placeKey !== '') return;
    let boss: { x: number; y: number; w: number; h: number } | null = null;
    const be = this.parent.querySelector<HTMLElement>('[data-testid="boss-bar"]');
    if (be && be.offsetWidth) { const r = viewRect(be, this.mr); boss = this.bossBox; boss.x = r.left; boss.y = r.top; boss.w = r.width; boss.h = r.height; }
    const lb = Math.round(this.leftBottom(W));
    let rows = 0;
    for (const r of this.rows) if (r.shown) rows++;
    const key = W + 'x' + H + ':' + u.toFixed(3) + ':' + lb + ':' + rows + ':' + (boss ? Math.round(boss.x) + ',' + Math.round(boss.w) : '-');
    if (key === this.placeKey) return;
    this.placeKey = key;
    const lr = raidLeaveRect(W, H, this.touch, boss);
    this.leaveBtn.style.left = Math.round(lr.x) + 'px';
    this.leaveBtn.style.top = Math.round(lr.y) + 'px';
    this.leaveBtn.style.height = Math.round(lr.h) + 'px';
    // 좁은 모양은 두 줄 글자(「정말 나가기?」도 두 줄)
    const narrow = lr.w < RAID_LEAVE_W;
    this.leaveBtn.style.width = Math.round(lr.w) + 'px';
    this.leaveBtn.style.whiteSpace = narrow ? 'normal' : 'nowrap';
    this.leaveBtn.style.font = (narrow ? '800 11px/1.15 ' : '800 12px ') + FONT;
    this.leaveBtn.style.padding = narrow ? '0 2px' : '0 6px';
    // 모양별 배율 전 크기를 한 번씩 잰다(줄 수가 바뀌면 다시)
    const hmH = this.heroMark.style.display === 'none' ? (this.heroMark.offsetHeight || 26) : 0;
    this.setMode('full');
    this.fullH = this.box.offsetHeight + hmH;
    this.setMode('compact');
    this.compactH = this.box.offsetHeight + hmH;
    this.setMode('mini');
    this.miniH = this.box.offsetHeight; this.miniW = this.box.offsetWidth;
    const tl = this.parent.querySelector<HTMLElement>('[data-testid="hud-top-left"]');
    const obj = this.parent.querySelector<HTMLElement>('[data-testid="hud-objective"]');
    const topLeft = this.topLeftBox;
    if (tl) { const t = viewRect(tl, this.mr); topLeft.x = t.left; topLeft.y = t.top; topLeft.w = t.width; topLeft.h = t.height; }
    else { topLeft.x = 6; topLeft.y = 4; topLeft.w = 0; topLeft.h = 66; }
    const objTop = obj ? viewRect(obj, this.mr).top : 112;
    const s0 = Math.max(0.62, u);
    const miniTop = raidMiniTop(topLeft, boss, this.miniW * s0, this.miniH * s0);
    const p = raidPartyLayout({ h: H, u, leftBottom: lb, fullH: this.fullH, compactH: this.compactH, miniH: this.miniH, miniTop, miniLimit: objTop - 2, short: H <= 420 && W >= H });
    this.setMode(p.mode);
    this.box.style.transform = 'scale(' + p.scale.toFixed(3) + ')';
    this.box.style.top = p.top + 'px';
    raidLayoutRev.n++;
  }

  /**
   * 파티 창 모양. full = 머리 줄 둘 + 용병 칸(초상·이름·HP) 세로, compact = 머리 줄 한 줄·작은 초상,
   * mini = 한 줄 가로(머리 + 용병마다 작은 초상과 짧은 HP 막대, 이름·글자 없음, 표적은 붉은 테두리). 값이 바뀔 때만 DOM을 고친다.
   */
  private setMode(m: RaidPartyMode): void {
    if (m === this.pmode) return;
    this.pmode = m;
    const mini = m === 'mini', small = m !== 'full';
    this.mode.style.display = small ? 'none' : '';
    this.head.style.padding = small ? '3px 8px' : '5px 9px';
    this.head.style.flexDirection = 'column';
    this.box.style.flexDirection = mini ? 'row' : 'column';
    this.box.style.alignItems = mini ? 'center' : '';
    this.box.style.width = mini ? 'max-content' : W + 'px';
    this.box.style.gap = small ? '3px' : '4px';
    const sz = mini ? '20px' : small ? '24px' : '32px';
    for (const r of this.rows) {
      r.pw.style.width = sz; r.pw.style.height = sz;
      r.box.style.padding = mini ? '2px 4px 2px 2px' : small ? '2px 6px 2px 3px' : '4px 8px 4px 4px';
      r.nl.style.display = mini ? 'none' : 'flex';
      r.bar.style.width = mini ? '34px' : '';
      // 한 줄 모양의 HP 숫자(9.5px)는 폰에서 읽히지 않는다(C8-8 비평가 P2) → 막대만
      r.hpText.style.display = mini ? 'none' : '';
      r.box.style.outline = '';
      r.key = ''; // 모양이 바뀌면 모양 의존 표시(미니의 표적 외곽선)를 다음 update가 다시 쓴다
    }
  }

}
